// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useState } from "react";
import { App, Alert, Button, Input, List, Popconfirm, Space, Tag, Typography } from "antd";
import { CopyOutlined, PlusOutlined } from "@ant-design/icons";
import { Homeserver } from "../api";
import type { RegistrationToken, Session } from "../types";

const hs = new Homeserver();

// inviteLink turns a raw token into the shareable sign-up URL the landing/login
// screen redeems (?reg=<token>).
function inviteLink(token: string): string {
  return `${globalThis.location.origin}/?reg=${encodeURIComponent(token)}`;
}

// InvitePanel lets an admin (or a member, when the server allows member-driven
// invites) mint LOCAL registration invites, see their outstanding ones, and revoke
// them. The minted secret is shown ONCE as a copyable link.
export function InvitePanel({ session }: { readonly session: Session }) {
  const { message } = App.useApp();
  const [tokens, setTokens] = useState<RegistrationToken[]>([]);
  const [fresh, setFresh] = useState(""); // a just-minted link, shown once
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      setTokens(await hs.listRegistrationTokens(session.access_token));
    } catch {
      // ignore — list is best-effort
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.access_token]);

  async function mint() {
    setBusy(true);
    try {
      const { token } = await hs.mintRegistrationToken(session.access_token);
      setFresh(inviteLink(token));
      await refresh();
    } catch (e) {
      message.error(`Could not create invite: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    try {
      await hs.revokeRegistrationToken(session.access_token, id);
      await refresh();
    } catch (e) {
      message.error(`Could not revoke: ${(e as Error).message}`);
    }
  }

  async function copy(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      message.success("Invite link copied");
    } catch {
      message.error("Copy failed — select and copy manually");
    }
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Invites are how people join this server. Each link is single-use by default and may expire — share
        it privately. The secret is shown once; only its fingerprint is kept here.
      </Typography.Paragraph>

      <Button type="primary" icon={<PlusOutlined />} loading={busy} onClick={mint} className="invite-create">
        Create invite link
      </Button>

      {fresh && (
        <Alert
          type="success"
          showIcon
          message="Invite link — copy it now"
          description={
            <Space.Compact style={{ width: "100%" }}>
              <Input readOnly value={fresh} className="invite-link" />
              <Button icon={<CopyOutlined />} onClick={() => copy(fresh)}>
                Copy
              </Button>
            </Space.Compact>
          }
        />
      )}

      <List
        size="small"
        header={<Typography.Text strong>Your invites</Typography.Text>}
        locale={{ emptyText: "No invites yet" }}
        dataSource={tokens}
        renderItem={(t) => {
          const exhausted = t.uses_used >= t.uses_allowed;
          const expired = t.expires_at !== undefined && t.expires_at < Date.now();
          return (
            <List.Item
              actions={[
                <Popconfirm key="revoke" title="Revoke this invite?" onConfirm={() => revoke(t.id)} okText="Revoke" okButtonProps={{ danger: true }}>
                  <Button size="small" danger type="text">
                    Revoke
                  </Button>
                </Popconfirm>,
              ]}
            >
              <Space size="small" wrap>
                <Typography.Text code>{t.id}</Typography.Text>
                <Tag color={exhausted ? "default" : "blue"}>
                  {t.uses_used}/{t.uses_allowed} used
                </Tag>
                {expired ? <Tag color="red">expired</Tag> : t.expires_at ? <Tag>expires {new Date(t.expires_at).toLocaleDateString()}</Tag> : <Tag>no expiry</Tag>}
              </Space>
            </List.Item>
          );
        }}
      />
    </Space>
  );
}
